import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';

interface Store {
  storeId?: string;
  name: string;
  // Test environment Stripe details
  stripeTestAccountId?: string;
  stripeTestAccountName?: string;
  stripeTestConnected: boolean;
  stripeTestConnectUrl?: string;
  stripeTestConnectUrlExpires?: Date | any;
  stripeTestOnboardingStarted?: Date | any;
  stripeTestOnboardingCompletedAt?: Date | any;
  stripeTestConnectedAt?: Date | any;
  // Live environment Stripe details
  stripeLiveAccountId?: string;
  stripeLiveAccountName?: string;
  stripeLiveConnected: boolean;
  stripeLiveConnectUrl?: string;
  stripeLiveConnectUrlExpires?: Date | any;
  stripeLiveOnboardingStarted?: Date | any;
  stripeLiveOnboardingCompletedAt?: Date | any;
  stripeLiveConnectedAt?: Date | any;
  // Legacy fields for backward compatibility
  stripeAccountId?: string;
  stripeAccountName?: string;
  stripeConnected: boolean;
  stripeConnectUrl?: string;
  stripeConnectUrlExpires?: Date | any;
  stripeOnboardingStarted?: Date | any;
  createdAt: Date | any;
  updatedAt: Date | any;
}

interface ExistingStripeAccount {
  accountId: string;
  accountName: string;
  storeName: string;
  storeId: string;
  organizationName?: string;
}

interface StripeConnectionModalProps {
  store: Store;
  appId: string;
  organizationId: string;
  environment: 'test' | 'live';
  db: Firestore;
  functions: Functions;
  onSuccess: (message: string) => void;
  onError: (error: string) => void;
  onCancel: () => void;
  setLoading: (loading: boolean) => void;
}

const StripeConnectionModal: React.FC<StripeConnectionModalProps> = ({
  store,
  appId,
  organizationId,
  environment,
  db,
  functions,
  onSuccess,
  onError,
  onCancel,
  setLoading
}) => {
  const [connectionType, setConnectionType] = useState<'new' | 'existing' | ''>('');
  const [newAccountName, setNewAccountName] = useState<string>('');
  const [selectedExistingAccount, setSelectedExistingAccount] = useState<string>('');
  const [existingAccounts, setExistingAccounts] = useState<ExistingStripeAccount[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [generatedConnectUrl, setGeneratedConnectUrl] = useState<string>('');
  const [showConnectUrl, setShowConnectUrl] = useState<boolean>(false);

  // Cloud function references
  const createStripeConnectAccountFunc = httpsCallable(functions, 'createStripeConnectAccount');
  const validateStripeAccountFunc = httpsCallable(functions, 'validateStripeAccount');
  const connectExistingStripeAccountFunc = httpsCallable(functions, 'connectExistingStripeAccount');
  const regenerateStripeConnectLinkFunc = httpsCallable(functions, 'regenerateStripeConnectLink');

  // Load existing Stripe accounts for this app
  useEffect(() => {
    const loadExistingAccounts = async () => {
      try {
        const q = query(
          collection(db, 'stores'),
          where('appId', '==', appId)
        );
        
        const querySnapshot = await getDocs(q);
        const accounts: ExistingStripeAccount[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (doc.id !== store.storeId) {
            // Add accounts based on environment
            if (environment === 'test' && data.stripeTestAccountId && data.stripeTestAccountName && data.stripeTestConnected) {
              accounts.push({
                accountId: data.stripeTestAccountId,
                accountName: data.stripeTestAccountName,
                storeName: data.name,
                storeId: doc.id,
                organizationName: data.stripeOrganization
              });
            } else if (environment === 'live' && data.stripeLiveAccountId && data.stripeLiveAccountName && data.stripeLiveConnected) {
              accounts.push({
                accountId: data.stripeLiveAccountId,
                accountName: data.stripeLiveAccountName,
                storeName: data.name,
                storeId: doc.id,
                organizationName: data.stripeOrganization
              });
            }
          }
        });

        // Remove duplicates
        const uniqueAccounts = accounts.filter((account, index, self) => 
          index === self.findIndex(a => a.accountId === account.accountId)
        );

        setExistingAccounts(uniqueAccounts);
      } catch (err) {
        console.error('Error loading existing accounts:', err);
      }
    };

    loadExistingAccounts();
  }, [db, appId, store.storeId]);

  // Generate account name from store name
  useEffect(() => {
    if (connectionType === 'new' && !newAccountName) {
      setNewAccountName(`${store.name} (${environment.toUpperCase()})`);
    }
  }, [connectionType, store.name, newAccountName, environment]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!connectionType) {
      newErrors.connectionType = 'Please select a connection type';
    } else if (connectionType === 'new') {
      if (!newAccountName.trim()) {
        newErrors.newAccountName = 'Account name is required';
      } else if (newAccountName.trim().length < 2) {
        newErrors.newAccountName = 'Account name must be at least 2 characters';
      }
    } else if (connectionType === 'existing') {
      if (!selectedExistingAccount) {
        newErrors.selectedExistingAccount = 'Please select an existing account';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle new Stripe account creation
  const handleCreateNewAccount = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const result = await createStripeConnectAccountFunc({
        appId: appId,
        storeId: store.storeId,
        accountName: newAccountName.trim(),
        environment: environment,
        returnUrl: `${window.location.origin}`,
        refreshUrl: `${window.location.origin}`
      });

      interface CreateStripeResponse {
        success: boolean;
        connectUrl: string;
        organizationName: string;
        message: string;
      }

      const responseData = result.data as CreateStripeResponse;
      
      if (responseData && responseData.success) {
        setGeneratedConnectUrl(responseData.connectUrl);
        setShowConnectUrl(true);
        onSuccess(`${responseData.message} Using ${responseData.organizationName} Stripe organization.`);
      } else {
        onError('Failed to create Stripe Connect account.');
      }
    } catch (err: any) {
      console.error('Error creating Stripe Connect account:', err);
      onError(err.message || 'Failed to start Stripe Connect process.');
    } finally {
      setLoading(false);
    }
  };

  // Handle existing account selection
  const handleSelectExistingAccount = () => {
    if (!validateForm()) return;
    setShowConfirmation(true);
  };

  // Validate existing Stripe account
  const validateExistingAccount = async (accountId: string): Promise<boolean> => {
    try {
      setIsValidating(true);
      const result = await validateStripeAccountFunc({ 
        accountId,
        organizationId,
        environment
      });
      
      interface ValidateResponse {
        valid: boolean;
        message?: string;
      }

      const responseData = result.data as ValidateResponse;
      
      if (responseData && responseData.valid) {
        return true;
      } else {
        onError(responseData?.message || 'The selected Stripe account has issues and cannot be used.');
        return false;
      }
    } catch (err: any) {
      console.error('Error validating Stripe account:', err);
      onError('Failed to validate Stripe account. Please try again.');
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  // Check if store already has a connect URL
  const hasExistingConnectUrl = () => {
    const connectUrl = environment === 'test' ? store.stripeTestConnectUrl : store.stripeLiveConnectUrl;
    const connected = environment === 'test' ? store.stripeTestConnected : store.stripeLiveConnected;
    const expires = environment === 'test' ? store.stripeTestConnectUrlExpires : store.stripeLiveConnectUrlExpires;
    
    if (!connectUrl || connected) return false;
    
    // Check if URL is expired
    if (expires) {
      const expiryDate = expires instanceof Date ? expires : expires.toDate();
      return new Date() < expiryDate;
    }
    
    return true;
  };

  // Regenerate expired connect URL
  const handleRegenerateLink = async () => {
    setLoading(true);
    try {
      const result = await regenerateStripeConnectLinkFunc({
        storeId: store.storeId,
        environment: environment,
        returnUrl: `${window.location.origin}`,
        refreshUrl: `${window.location.origin}`
      });

      interface RegenerateResponse {
        success: boolean;
        connectUrl: string;
        message: string;
      }

      const responseData = result.data as RegenerateResponse;
      
      if (responseData && responseData.success) {
        setGeneratedConnectUrl(responseData.connectUrl);
        setShowConnectUrl(true);
        onSuccess(responseData.message || 'New Stripe Connect link generated successfully!');
      } else {
        onError('Failed to regenerate Stripe Connect link.');
      }
    } catch (err: any) {
      console.error('Error regenerating Stripe Connect link:', err);
      onError(err.message || 'Failed to regenerate Stripe Connect link.');
    } finally {
      setLoading(false);
    }
  };

  // Copy URL to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onSuccess('Link copied to clipboard!');
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      onSuccess('Link copied to clipboard!');
    }
  };

  const handleConnectExistingAccount = async () => {
    const selectedAccount = existingAccounts.find(acc => acc.accountId === selectedExistingAccount);
    if (!selectedAccount) return;

    // Validate the account first
    const isValid = await validateExistingAccount(selectedAccount.accountId);
    if (!isValid) return;

    setLoading(true);
    try {
      const result = await connectExistingStripeAccountFunc({
        storeId: store.storeId,
        stripeAccountId: selectedAccount.accountId,
        stripeAccountName: selectedAccount.accountName,
        environment: environment,
        organizationId: organizationId
      });

      interface ConnectExistingResponse {
        success: boolean;
        message: string;
      }

      const responseData = result.data as ConnectExistingResponse;
      
      if (responseData && responseData.success) {
        onSuccess(responseData.message || `Store "${store.name}" has been connected to the existing Stripe account "${selectedAccount.accountName}".`);
        onCancel(); // Close modal
      } else {
        onError('Failed to connect existing Stripe account.');
      }
    } catch (err: any) {
      console.error('Error connecting existing Stripe account:', err);
      onError(err.message || 'Failed to connect existing Stripe account.');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = existingAccounts.find(acc => acc.accountId === selectedExistingAccount);

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-container stripe-connection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Connect {environment.toUpperCase()} Stripe Account</h3>
          <button className="modal-close" onClick={onCancel}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="store-info">
            <h4>Store: {store.name}</h4>
            <div className="environment-info">
              <strong>Environment:</strong> {environment.toUpperCase()}
              <span className={`environment-badge ${environment}`}>
                {environment === 'test' ? '🧪 Test Mode' : '🔴 Live Mode'}
              </span>
            </div>
            <p>Choose how you want to connect a {environment} Stripe account for payment processing.</p>
          </div>

          {!showConfirmation && !showConnectUrl ? (
            <div className="connection-options">
              {/* Check if store already has a pending connect URL */}
              {hasExistingConnectUrl() && (
                <div className="existing-connect-url">
                  <h5>Existing {environment.toUpperCase()} Stripe Connect Link</h5>
                  <div className="connect-url-section">
                    <p>This store already has a pending {environment} Stripe Connect link:</p>
                    <div className="url-display">
                      <input
                        type="text"
                        value={environment === 'test' ? (store.stripeTestConnectUrl || '') : (store.stripeLiveConnectUrl || '')}
                        readOnly
                        className="url-input"
                      />
                      <button
                        type="button"
                        className="copy-btn"
                        onClick={() => copyToClipboard(environment === 'test' ? (store.stripeTestConnectUrl || '') : (store.stripeLiveConnectUrl || ''))}
                      >
                        Copy
                      </button>
                    </div>
                    <div className="url-actions">
                      <button
                        type="button"
                        className="regenerate-btn"
                        onClick={handleRegenerateLink}
                      >
                        Generate New Link
                      </button>
                      <button
                        type="button"
                        className="open-link-btn"
                        onClick={() => window.open(environment === 'test' ? store.stripeTestConnectUrl : store.stripeLiveConnectUrl, '_blank')}
                      >
                        Open Link
                      </button>
                    </div>
                    {((environment === 'test' && store.stripeTestConnectUrlExpires) || (environment === 'live' && store.stripeLiveConnectUrlExpires)) && (
                      <small className="expiry-info">
                        Expires: {new Date((environment === 'test' ? store.stripeTestConnectUrlExpires : store.stripeLiveConnectUrlExpires) instanceof Date 
                          ? (environment === 'test' ? store.stripeTestConnectUrlExpires : store.stripeLiveConnectUrlExpires)
                          : (environment === 'test' ? store.stripeTestConnectUrlExpires : store.stripeLiveConnectUrlExpires).toDate()
                        ).toLocaleString()}
                      </small>
                    )}
                  </div>
                </div>
              )}

              <div className="connection-type-selection">
                <h5>Select Connection Type</h5>
                
                <div className="radio-group">
                  {existingAccounts.length > 0 && (
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="connectionType"
                        value="existing"
                        checked={connectionType === 'existing'}
                        onChange={(e) => setConnectionType(e.target.value as 'existing')}
                      />
                      <div className="radio-content">
                        <h6>Use Existing {environment.toUpperCase()} Stripe Account</h6>
                        <p>Connect to a {environment} Stripe account already used by another store in this app.</p>
                      </div>
                    </label>
                  )}
                  
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="connectionType"
                      value="new"
                      checked={connectionType === 'new'}
                      onChange={(e) => setConnectionType(e.target.value as 'new')}
                    />
                    <div className="radio-content">
                      <h6>Connect to a New Stripe Account</h6>
                      <p>Create or connect to a different Stripe account for this store.</p>
                    </div>
                  </label>
                </div>

                {errors.connectionType && (
                  <span className="error-text">{errors.connectionType}</span>
                )}
              </div>

              {connectionType === 'existing' && existingAccounts.length > 0 && (
                <div className="existing-account-selection">
                  <h5>Select Existing Account</h5>
                  <select
                    value={selectedExistingAccount}
                    onChange={(e) => setSelectedExistingAccount(e.target.value)}
                    className={errors.selectedExistingAccount ? 'error' : ''}
                  >
                    <option value="">Choose an account...</option>
                    {existingAccounts.map((account) => (
                      <option key={account.accountId} value={account.accountId}>
                        {account.accountName} (used by {account.storeName})
                      </option>
                    ))}
                  </select>
                  {errors.selectedExistingAccount && (
                    <span className="error-text">{errors.selectedExistingAccount}</span>
                  )}
                </div>
              )}

              {connectionType === 'new' && (
                <div className="new-account-setup">
                  <h5>Account Name</h5>
                  <input
                    type="text"
                    value={newAccountName}
                    onChange={(e) => setNewAccountName(e.target.value)}
                    placeholder="Enter a name for this Stripe account"
                    className={errors.newAccountName ? 'error' : ''}
                  />
                  {errors.newAccountName && (
                    <span className="error-text">{errors.newAccountName}</span>
                  )}
                  <small className="form-help-text">
                    This name will help you identify this Stripe account when managing multiple stores.
                  </small>
                </div>
              )}
            </div>
          ) : showConnectUrl ? (
            <div className="connect-url-generated">
              <div className="success-message">
                <h4>✅ Stripe Connect Link Generated</h4>
                <p>Use this link to complete your Stripe account setup:</p>
              </div>
              
              <div className="url-display">
                <input
                  type="text"
                  value={generatedConnectUrl}
                  readOnly
                  className="url-input"
                />
                <button
                  type="button"
                  className="copy-btn"
                  onClick={() => copyToClipboard(generatedConnectUrl)}
                >
                  Copy
                </button>
              </div>
              
              <div className="url-actions">
                <button
                  type="button"
                  className="open-link-btn primary"
                  onClick={() => window.open(generatedConnectUrl, '_blank')}
                >
                  Open Stripe Connect
                </button>
              </div>
              
              <div className="connect-instructions">
                <h5>Next Steps:</h5>
                <ol>
                  <li>Click "Open Stripe Connect" or copy the link</li>
                  <li>Complete the Stripe onboarding process</li>
                  <li>Return to this app when finished</li>
                  <li>Your store will be automatically activated for payments</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="confirmation-section">
              <div className="confirmation-header">
                <div className="confirmation-icon">⚠️</div>
                <h4>Confirm Account Connection</h4>
              </div>
              
              <div className="confirmation-details">
                <p>You are about to connect <strong>"{store.name}"</strong> to the existing Stripe account:</p>
                
                <div className="account-details">
                  <div className="detail-item">
                    <label>Account Name:</label>
                    <span>{selectedAccount?.accountName}</span>
                  </div>
                  <div className="detail-item">
                    <label>Currently used by:</label>
                    <span>{selectedAccount?.storeName}</span>
                  </div>
                </div>

                <div className="warning-message">
                  <p><strong>Important:</strong> Both stores will receive payments to the same bank account. Make sure this is what you want.</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          {!showConfirmation && !showConnectUrl ? (
            <>
              {connectionType === 'new' && (
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleCreateNewAccount}
                  disabled={!connectionType || !!Object.keys(errors).length}
                >
                  Generate Stripe Connect Link
                </button>
              )}
              
              {connectionType === 'existing' && (
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleSelectExistingAccount}
                  disabled={!selectedExistingAccount || !!Object.keys(errors).length}
                >
                  Continue with Selected Account
                </button>
              )}
              
              <button
                type="button"
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </button>
            </>
          ) : showConnectUrl ? (
            <button
              type="button"
              className="submit-btn"
              onClick={onCancel}
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="submit-btn"
                onClick={handleConnectExistingAccount}
                disabled={isValidating}
              >
                {isValidating ? 'Validating...' : 'Confirm Connection'}
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => setShowConfirmation(false)}
              >
                Go Back
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeConnectionModal;